"use client"

import { useState, useEffect } from "react"
// import { Card, CardContent } from "@/components/ui/card" 
// import { Textarea } from "@/components/ui/textarea"   
// import { Button } from "@/components/ui/button"     
import { Edit, Save, CheckCircle, XCircle } from "lucide-react"

// Placeholder components
const Card = ({ children, className, ...props }) => <div className={`border rounded-lg shadow-md ${className}`} {...props}>{children}</div>;
const CardContent = ({ children, className, ...props }) => <div className={`p-4 ${className}`} {...props}>{children}</div>;
const Textarea = ({ className, ...props }) => <textarea className={`border p-2 rounded-md w-full ${className}`} {...props} />;
const Button = ({ children, className, variant, ...props }) => {
  let baseStyle = "px-4 py-2 rounded-md font-semibold flex items-center justify-center gap-2 transition-colors duration-150";
  if (variant === "outline") {
    baseStyle += " border border-gray-300 text-gray-700 hover:bg-gray-100";
  } else if (variant === "destructive") {
    baseStyle += " bg-red-500 text-white hover:bg-red-600";
  } else if (props.className && props.className.includes('bg-green-600')) { // Specific for confirm button
    baseStyle += " bg-green-600 text-white hover:bg-green-700";
  } else { // Default variant
    baseStyle += " bg-blue-600 text-white hover:bg-blue-700";
  }
  return <button className={`${baseStyle} ${className}`} {...props}>{children}</button>;
};

const BinGridPreview = ({ initialBinData, onConfirm, onCancel, isVisible }) => {
  const [binData, setBinData] = useState([]);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    // Initialize with a deep copy to prevent direct mutation of prop
    if (initialBinData) {
      setBinData(initialBinData.map(row => [...row]));
    } else {
      setBinData([]); // Handle case where initialBinData might be null or undefined
    }
  }, [initialBinData]);

  if (!isVisible) {
    return null;
  }

  // Simple color pattern for visual distinction
  const binColors = [
    "bg-blue-50 border-blue-200 hover:bg-blue-100",
    "bg-emerald-50 border-emerald-200 hover:bg-emerald-100",
    "bg-amber-50 border-amber-200 hover:bg-amber-100",
    "bg-purple-50 border-purple-200 hover:bg-purple-100",
    "bg-rose-50 border-rose-200 hover:bg-rose-100",
    "bg-cyan-50 border-cyan-200 hover:bg-cyan-100",
  ];

  const getBinColor = (rowIndex, colIndex) => {
    const colorIndex = (rowIndex + colIndex) % binColors.length;
    return binColors[colorIndex];
  };

  const handleEdit = (rowIndex, colIndex, value) => {
    const newData = binData.map(row => [...row]); // Ensure working with a copy
    newData[rowIndex][colIndex] = value;
    setBinData(newData);
  };

  const handleConfirm = () => {
    setIsEditing(false); // Exit editing mode if active
    onConfirm(binData.map(row => [...row])); // Pass a deep copy
  };

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
  };
  
  const numRows = binData.length;
  const numCols = numRows > 0 ? binData[0].length : 0;


  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
      <div className="p-6 bg-gradient-to-b from-gray-50 to-gray-100 rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <div className="flex-1 min-w-0">
            <h2 className="text-3xl font-bold text-gray-800">Bin Grid Preview & Edit</h2>
            <p className="text-gray-500 mt-1">
              Review your bin locations. Parsed as {numRows} rows and {numCols} columns.
            </p>
          </div>

          <div className="flex flex-row flex-1 justify-end items-center gap-4 w-full md:w-auto">
            <Button onClick={handleToggleEdit} variant="outline" className="gap-2 px-4 py-2 text-base min-w-[160px] flex-1">
              {isEditing ? <Save className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
              {isEditing ? "Save Edits" : "Edit Bins"}
            </Button>
            <Button onClick={handleConfirm} variant="default" className="gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 text-base min-w-[160px] flex-1">
              <CheckCircle className="h-4 w-4" /> Confirm Parts
            </Button>
            <Button onClick={onCancel} variant="destructive" className="gap-2 px-4 py-2 text-base min-w-[160px] flex-1">
              <XCircle className="h-4 w-4" /> Cancel
            </Button>
          </div>
        </div>

        <div className="overflow-auto flex-grow">
          {numRows === 0 || numCols === 0 ? (
            <div className="flex items-center justify-center h-full text-gray-500">
                No data to display. The uploaded CSV might be empty or incorrectly formatted.
            </div>
          ) : (
            <div 
                className="grid gap-2 p-1 bg-gray-200 rounded-md"
                style={{
                    gridTemplateColumns: `repeat(${numCols}, minmax(100px, 1fr))`,
                    gridTemplateRows: `repeat(${numRows}, minmax(80px, auto))`,
                }}
            >
            {binData.map((row, rowIndex) =>
              row.map((cell, colIndex) => {
                const binKey = `${rowIndex}-${colIndex}`;
                return (
                  <Card
                    key={binKey}
                    className={`relative transition-all duration-200 
                      ${getBinColor(rowIndex, colIndex)} 
                      border-2 shadow-sm hover:shadow-md overflow-hidden 
                      flex flex-col justify-center`} // Ensure card itself can center content
                    style={{ perspective: "1000px" }}
                  >
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-10 h-1 bg-gray-300 rounded-b-md"></div>
                    <CardContent className="p-0 flex-grow flex flex-col"> {/* CardContent takes full height */}
                      <div className="relative h-full flex flex-col"> {/* Inner div takes full height */}
                        <div className="p-2 pt-4 flex-grow flex flex-col items-center justify-center"> {/* Content area */}
                          {isEditing ? (
                            <Textarea
                              className="text-center bg-white/80 border-gray-300 font-medium resize-none w-full h-full text-xs"
                              value={cell}
                              placeholder="(Empty)"
                              onChange={(e) => handleEdit(rowIndex, colIndex, e.target.value)}
                            />
                          ) : (
                            <div className="flex flex-col items-center justify-center h-full text-center">
                              <span className="font-medium text-gray-800 text-sm break-words">
                                {cell || <span className="text-gray-400 italic">(Empty Bin)</span>}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              }),
            )}
            </div>
          )}
        </div>

        <div className="mt-4 text-center text-gray-500 text-xs">
          <p>Ensure bin contents match your physical setup. Empty bins are shown as <span className="italic">(Empty Bin)</span>.</p>
        </div>
      </div>
    </div>
  );
};

export default BinGridPreview; 